import { ref, Ref, toRefs, reactive } from 'vue'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import useConfiguration from '@/composables/configuration/configuration'
import ApiValidationError from '@/error/apiValidationError'
import ApiError from '@/error/apiError'
import { StringHelper } from '@/helper/string'
import useApiTimeout, { ApiTimeout } from '@/composables/apiTimeout'
import useAppState from '@/composables/ui/ui'
import AppError, { APP_ERRORS } from '@/error/appError'

export type ApiRequest = () => Promise<void>

export enum StatusCodes {
  OK = 200,
  CREATED = 201,
  NOT_FOUND = 404,
  UNPROCESSABLE_ENTITY = 422,
  INTERNAL_SERVER_ERROR = 500,
}

export enum ORDER {
  DESC = 'desc',
  ASC = 'asc',
}

class ApiState<T> {
  response: Ref<T | null> = ref(null)
  loading: Ref<boolean> = ref(false)
  error = ref<Error>()
  count = 0
  statusCode: number = StatusCodes.INTERNAL_SERVER_ERROR
}

const { canCallApi, addTimeout } = useApiTimeout()
const { appError } = useAppState()
let client: AxiosInstance | undefined = undefined

function getClient(): AxiosInstance {
  if (typeof client === 'undefined') {
    const { configuration } = useConfiguration()

    client = axios.create({
      baseURL: configuration.envConf.forumUrl,
      timeout: 30 * 1000,
      headers: {
        'Content-Type': 'application/json',
        'x-app-version': 'forum-widget-' + configuration.appVersion,
      },
      withCredentials: true,
    })
  }

  return client
}

function createConstraints(
  fields: Record<string, any>
): Map<string, Array<string>> {
  const constraints: Map<string, Array<string>> = new Map()

  for (const [field, error] of Object.entries(fields)) {
    constraints.set(
      field,
      error.map(
        (errorTitle: string) =>
          'error.' + StringHelper.underscoreToCamel(errorTitle)
      )
    )
  }

  return constraints
}

function createError(err: any): Error {
  if (err.response.status === 422) {
    const constraints = createConstraints(err.response.data.fields)
    return new ApiValidationError(constraints)
  } else {
    return new ApiError(
      StringHelper.underscoreToCamel(err.response.data.error),
      StringHelper.underscoreToCamel(err.response.data.detail)
    )
  }
}

export default function useApi<T>(url: string, options?: AxiosRequestConfig) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    try {
      const response = await getClient().get(url, options)
      apiState.response = response.data // todo
      apiState.statusCode = response.status
    } catch (err) {
      apiState.error = err
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}

export function useApiList<T>(
  url: string,
  options?: AxiosRequestConfig,
  apiTimeout?: ApiTimeout
) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    if (apiTimeout && !canCallApi(apiTimeout)) {
      return
    }
    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    try {
      const response = await getClient().get(url, options)

      apiState.response =
        typeof response.data.data === 'undefined'
          ? response.data
          : response.data.data
      apiState.count = response.data.totalCount
      apiState.statusCode = response.status

      // @ts-ignore
      if (apiTimeout && apiState.response.length < options?.params.limit) {
        addTimeout(apiTimeout)
      }
    } catch (err) {
      apiState.error = err
      if (err.response.status >= 500) {
        appError.value = new AppError(APP_ERRORS.INTERNAL_SERVER_ERROR)
      }
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}

export function useFirstFromList<T>(url: string, options?: AxiosRequestConfig) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    try {
      const response = await getClient().get(url, options)

      apiState.statusCode = response.status

      if (response.data.data.length > 0) {
        apiState.response = response.data.data[0]
      }
    } catch (err) {
      apiState.error = err
      if (err.response.status >= 500) {
        appError.value = new AppError(APP_ERRORS.INTERNAL_SERVER_ERROR)
      }
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}

export function useApiSingle<T>(url: string, options?: AxiosRequestConfig) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    try {
      const response = await getClient().get(url, options)
      apiState.response = response.data
      apiState.statusCode = response.status
    } catch (err) {
      apiState.error = err
      if (err.response.status >= 500) {
        appError.value = new AppError(APP_ERRORS.INTERNAL_SERVER_ERROR)
      }
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}

export function useApiCreate<T>(
  url: string,
  object: Record<string, any>,
  options?: AxiosRequestConfig,
  apiTimeout?: ApiTimeout
) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    const { configuration } = useConfiguration()
    if (configuration.extSysConf.isWriteDisabled()) {
      return
    }
    if (apiTimeout && !canCallApi(apiTimeout)) {
      return
    }

    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    try {
      const response = await getClient().post(
        url,
        JSON.stringify(object),
        options
      )

      apiState.response = response.data
      apiState.statusCode = response.status
      if (
        apiTimeout &&
        apiTimeout.responseCodes.includes(apiState.statusCode)
      ) {
        addTimeout(apiTimeout)
      }
    } catch (err) {
      apiState.error = createError(err)
      apiState.statusCode = err.status
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}

export function useApiUpdate<T>(
  url: string,
  object: Record<string, any>,
  options?: AxiosRequestConfig,
  apiTimeout?: ApiTimeout
) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    const { configuration } = useConfiguration()
    if (configuration.extSysConf.isWriteDisabled()) {
      return
    }

    if (apiTimeout && !canCallApi(apiTimeout)) {
      return
    }

    try {
      const response = await getClient().put(
        url,
        JSON.stringify(object),
        options
      )

      apiState.response = response.data
      apiState.statusCode = response.status
      if (apiTimeout && response.status === StatusCodes.OK) {
        addTimeout(apiTimeout)
      }
    } catch (err) {
      apiState.error = createError(err)
      apiState.statusCode = err.status
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}

export function useApiDelete<T>(url: string, options?: AxiosRequestConfig) {
  const apiState = reactive<ApiState<T>>(new ApiState())

  const request: ApiRequest = async () => {
    apiState.loading = true
    apiState.error = undefined
    apiState.response = null

    const { configuration } = useConfiguration()
    if (configuration.extSysConf.isWriteDisabled()) {
      return
    }

    try {
      const response = await getClient().delete(url, options)
      apiState.response = response.data
      apiState.statusCode = response.status
    } catch (err) {
      apiState.error = createError(err)
    } finally {
      apiState.loading = false
    }
  }
  return { ...toRefs(apiState), request }
}
